import axios from 'axios';
import config from '../config.json';

export interface User {
  id: string;
  email: string;
  firstname?: string;
  lastname?: string;
  access_token: string;
  refresh_token?: string;
  token: string;
  auth: {
    admin_group: boolean;
    billing_group: boolean;
    technical_group: boolean;
  };
  exp: number;
  expireDate: Date;
  account_id?: number;
  pk?: number;
  time_zone?: string; // Added timezone field
  account?: any; // For billing_api_id and other account data
}

export interface LoginRequest {
  username: string;
  password: string;
}

export interface LoginResponse {
  result: {
    access_token: string;
    refresh_token?: string;
    admin_group: boolean;
    billing_group: boolean;
    email: string;
    firstname: string;
    lastname: string;
    pk: number;
    account_id: number;
    technical_group?: boolean;
    time_zone?: string;
    ui_color_mode?: string;
    account?: {
      billing_api_id: string;
      name: string;
      pk: number;
      [key: string]: any;
    };
  };
}

class AuthService {
  private baseUrl: string;

  constructor() {
    this.baseUrl = config.prod ? config.urlBase.prod : config.urlBase.dev;
  }

  async login(username: string, password: string): Promise<User> {
    const response = await axios.post<LoginResponse>(`${this.baseUrl}/crm/auth/login`, {
      // Autofill and copy-paste often leave a trailing space on the email.
      username: username.trim(),
      password,
    });
    
    const result = response.data.result;
    const user = this.saveTokenInLocalStorage(result);
    // Clear impersonating flag for normal login
    localStorage.removeItem('impersonating');
    return user;
  }

  async refresh(token: string): Promise<User> {
    const response = await axios.get<LoginResponse>(`${this.baseUrl}/crm/auth/refresh`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    
    const result = response.data.result;
    const user = this.saveTokenInLocalStorage(result);
    return user;
  }

  /**
   * Redirect to the backend Google OAuth endpoint. The backend runs the full
   * OAuth flow against the same Google client used by the Voice360 portal and
   * redirects back to {origin}/login?token=... (handled by App's token effect).
   */
  initiateGoogleSSO(): void {
    const redirectUrl = `${window.location.origin}/login`;
    window.location.href =
      `${this.baseUrl}/crm/auth/google/login?redirect_url=${encodeURIComponent(redirectUrl)}`;
  }

  /**
   * Redirect to the backend Microsoft OAuth endpoint (same pattern as Google).
   */
  initiateMicrosoftSSO(): void {
    const redirectUrl = `${window.location.origin}/login`;
    window.location.href =
      `${this.baseUrl}/crm/auth/microsoft/login?redirect_url=${encodeURIComponent(redirectUrl)}`;
  }

  /**
   * Login using a token (impersonate flow, and the SSO callback).
   * @param impersonate when true (default) mark the session as an impersonation
   *   session; SSO logins pass false so they behave like a normal login.
   */
  async loginWithToken(token: string, impersonate: boolean = true): Promise<User> {
    // For impersonate, we just validate the token and save it
    // The token is already generated by the CRM with the correct user info
    try {
      // Decode the JWT to get user info
      const payload = JSON.parse(atob(token.split('.')[1]));

      // Extract contact info from the JWT payload (it's in user_contact)
      const contact = payload.user_contact || {};
      
      // Extract account info from the JWT payload (it's in account)
      const account = payload.account || null;
      
      // Create the result object that saveTokenInLocalStorage expects
      const result = {
        access_token: token,
        refresh_token: token,
        admin_group: contact.admin_group || false,
        billing_group: contact.billing_group || false,
        email: contact.email || contact.mail,
        firstname: contact.firstname || contact.first_name,
        lastname: contact.lastname || contact.last_name,
        pk: parseInt(payload.id) || contact.pk,
        account_id: contact.account_id,
        technical_group: contact.technical_group || false,
        time_zone: contact.time_zone || null,
        ui_color_mode: contact.ui_color_mode || null,
        account: account, // Use account from JWT payload instead of contact.account
      };
      
      const user = this.saveTokenInLocalStorage(result);

      // Mark this as an impersonation session (SSO logins pass impersonate=false)
      if (impersonate) {
        localStorage.setItem('impersonating', 'true');
      } else {
        localStorage.removeItem('impersonating');
      }
      return user;
    } catch (error) {
      throw new Error('Invalid token for impersonate login');
    }
  }

  logout(): void {
    localStorage.removeItem('userDetails');
    localStorage.removeItem('impersonating');
    // Redirect to logout URL if needed
    const logoutUrl = config.prod 
      ? `${config.portal.prod}?logout=true`
      : `${config.portal.dev}?logout=true`;
    
    // Only redirect if we're not on localhost (any port)
    const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    
    if (!isLocalhost) {
      window.location.href = logoutUrl;
    }
  }

  getCurrentUser(): User | null {
    const userDetails = localStorage.getItem('userDetails');
    if (!userDetails) return null;
    
    try {
      const user = JSON.parse(userDetails);
      
      // Check if token is expired
      if (user.expireDate && new Date() > new Date(user.expireDate)) {
        this.logout();
        return null;
      }
      
      return user;
    } catch {
      return null;
    }
  }

  isAuthenticated(): boolean {
    const user = this.getCurrentUser();
    return !!user?.access_token;
  }

  isAdmin(): boolean {
    const user = this.getCurrentUser();
    return !!user?.auth?.admin_group;
  }

  canAccessBilling(): boolean {
    const user = this.getCurrentUser();
    return !!user?.auth?.billing_group;
  }

  private saveTokenInLocalStorage(result: LoginResponse['result']): User {
    // Decode JWT token to get expiration time
    const base64Url = result.access_token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
      return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));
    
    const decodedToken = JSON.parse(jsonPayload);
    
    const user: User = {
      id: result.pk.toString(),
      email: result.email,
      firstname: result.firstname,
      lastname: result.lastname,
      access_token: result.access_token,
      refresh_token: result.refresh_token,
      token: result.access_token,
      auth: {
        admin_group: result.admin_group,
        billing_group: result.billing_group,
        technical_group: result.technical_group || false,
      },
      exp: decodedToken.exp,
      expireDate: new Date(decodedToken.exp * 1000),
      account_id: result.account_id,
      pk: result.pk,
      account: result.account, // Include the full account object
      time_zone: result.time_zone,
    };

    localStorage.setItem('userDetails', JSON.stringify(user));
    return user;
  }

  getAuthToken(): string | null {
    const user = this.getCurrentUser();
    return user?.access_token || null;
  }

  setUser(updatedUser: User): void {
    localStorage.setItem('userDetails', JSON.stringify(updatedUser));
  }
}

export const authService = new AuthService();